import { Portal } from '@mantine/core';
import { useDisclosure, usePrevious } from '@mantine/hooks';
import { some } from 'lodash-es';
import type { EditorState } from 'prosemirror-state';
import { NodeSelection, TextSelection } from 'prosemirror-state';
import { useCallback, useEffect } from 'react';
import type { ISecodaEditor, MenuItem } from '../..';
import dictionary from '../../dictionary';
import useEventListener from '../../hooks/useEventListener';
import filterExcessSeparators from '../../lib/filterExcessSeparators';
import getDividerMenuItems from '../../menus/divider';
import getFormattingMenuItems from '../../menus/formatting';
import getImageMenuItems from '../../menus/image';
import getTableMenuItems from '../../menus/table';
import getTableColMenuItems from '../../menus/tableCol';
import getTableRowMenuItems from '../../menus/tableRow';
import getViewerFormattingMenuItems from '../../menus/viewerFormatting';
import getMarkRange from '../../queries/getMarkRange';
import isMarkActive from '../../queries/isMarkActive';
import isNodeActive from '../../queries/isNodeActive';
import { getColumnIndex, getRowIndex } from '../../queries/table';
import { Toolbar } from '../../types';
import { FloatingToolbar } from './FloatingToolbar';
import LinkEditor from './LinkEditor';
import { ToolbarMenu } from './ToolbarMenu';

function useIsActive(state: EditorState | undefined) {
	if (!state) {
		return false;
	}

	const { selection, doc } = state;

	if (isMarkActive(state.schema.marks.link)(state)) {
		return true;
	}
	if (
		(isNodeActive(state.schema.nodes.code_block)(state) ||
			isNodeActive(state.schema.nodes.code_fence)(state)) &&
		selection.from > 0
	) {
		return true;
	}

	// if (isInNotice(state) && selection.from > 0) {
	//   return true;
	// }

	if (!selection || selection.empty) {
		return false;
	}
	if (selection instanceof NodeSelection && selection.node.type.name === 'hr') {
		return true;
	}
	if (
		selection instanceof NodeSelection &&
		['image', 'attachment'].includes(selection.node.type.name)
	) {
		return true;
	}
	if (selection instanceof NodeSelection) {
		return false;
	}

	const selectionText = doc.cut(selection.from, selection.to).textContent;
	if (selection instanceof TextSelection && !selectionText) {
		return false;
	}

	const slice = selection.content();
	const fragment = slice.content;
	const nodes = (fragment as any).content;

	return some(nodes, (n) => n.content.size);
}

function useIsDragging() {
	// const [isDragging, setDragging, setNotDragging] = useBoolean();
	const [isDragging, { open, close }] = useDisclosure(false);

	useEventListener('dragstart', open);
	useEventListener('dragend', close);
	useEventListener('drop', close);
	return isDragging;
}

export function SelectionToolbar({
	isTemplate,
	isRestrictedToComments,
	commentsEnabled,
	isOpen,
	editorState,
}: {
	isTemplate: boolean;
	isRestrictedToComments: boolean;
	commentsEnabled: boolean;
	isOpen: boolean;
	editorState: ISecodaEditor;
}) {
	const isActive = useIsActive(editorState.view?.state);
	const isDragging = useIsDragging();
	const previousIsActive = usePrevious(isActive);

	useEffect(() => {
		// Trigger callbacks when the toolbar is opened or closed
		if (previousIsActive && !isActive) {
			editorState.closeToolbars(Toolbar.Selection);
		}
		if (!previousIsActive && isActive) {
			editorState.openToolbar(Toolbar.Selection);
		}
	}, [isActive, editorState, previousIsActive]);

	const handleOnSelectLink = useCallback(
		({ href, from, to }: { href: string; from: number; to: number }): void => {
			if (!editorState.view) {
				return;
			}

			const linkMarkType = editorState.view.state.schema.marks.link;

			if (!linkMarkType) {
				throw new Error("Mark 'link' not found");
			}

			editorState.view.dispatch(
				editorState.view.state.tr
					.removeMark(from, to, linkMarkType)
					.addMark(from, to, linkMarkType.create({ href }))
			);
		},
		[editorState.view]
	);

	if (
		(editorState.options.readOnly && !commentsEnabled) ||
		isDragging ||
		!editorState.view ||
		!isOpen
	) {
		return null;
	}

	const { state } = editorState.view;
	const { selection } = state;

	const isCodeSelection = isNodeActive(state.schema.nodes.code_block)(state);
	const isDividerSelection = isNodeActive(state.schema.nodes.hr)(state);

	// Toolbar is disabled in code blocks, no bold / italic etc
	if (isCodeSelection) {
		return null;
	}

	const colIndex = getColumnIndex(state);
	const rowIndex = getRowIndex(state);
	const isTableSelection = colIndex !== undefined && rowIndex !== undefined;
	const link = isMarkActive(state.schema.marks.link)(state);
	const range = getMarkRange(selection.$from, state.schema.marks.link);
	const isImageSelection =
		selection instanceof NodeSelection && selection.node.type.name === 'image';

	let items: MenuItem[] = [];
	if (isTableSelection) {
		items = getTableMenuItems(dictionary);
	} else if (colIndex !== undefined) {
		items = getTableColMenuItems(state, colIndex, false, dictionary);
	} else if (rowIndex !== undefined) {
		items = getTableRowMenuItems(state, rowIndex, dictionary);
	} else if (isImageSelection) {
		items = getImageMenuItems(state, dictionary);
	} else if (isDividerSelection) {
		items = getDividerMenuItems(state, dictionary);
	} else if (isRestrictedToComments) {
		items = getViewerFormattingMenuItems(state, dictionary, commentsEnabled);
	} else {
		items = getFormattingMenuItems(state, isTemplate, dictionary);
	}

	// Some extensions may be disabled, remove corresponding items
	items = items?.filter((item) => {
		if (item.name === 'separator') return true;
		if (item.name && !editorState.commands[item.name] && !item.menuOptions)
			return false;
		if (item.visible === false) return false;
		return true;
	});

	items = filterExcessSeparators(items);

	if (!items.length) {
		return null;
	}

	return (
		<Portal>
			<FloatingToolbar
				view={editorState.view}
				onClose={() => editorState.closeToolbars(Toolbar.Selection)}
			>
				{link && range ? (
					<LinkEditor
						editor={editorState}
						mark={range.mark}
						from={range.from}
						to={range.to}
						onSelectLink={handleOnSelectLink}
					/>
				) : (
					<ToolbarMenu items={items} />
				)}
			</FloatingToolbar>
		</Portal>
	);
}
